import { markScrollAsProgrammatic } from '../programmatic-scroll-detector'

/**
 * Utility function that provides intelligent scroll assistance for search filter interactions.
 *
 * This function creates device-aware scrolling behavior that enhances UX when users interact
 * with filters or sort. It ensures users are positioned at the most relevant content
 * after applying filters or changing sort, with different strategies for mobile(RepFin) and desktop(Sort).
 *
 * Behavior:
 * - Mobile (< 768px): Scrolls to representative example heading to show financial context
 * - Desktop (>= 768px): Scrolls to sort filter with precise 50px positioning and smart logic
 *
 * Desktop features:
 * - Position checking: Won't scroll if sort filter already well-positioned (20px-80px from top)
 * - Short page detection: Scrolls to top if insufficient content to scroll properly
 * - Precise positioning: Places sort filter exactly 50px from viewport top
 * - Fallback handling: Uses viewport-relative scroll if target elements not found
 */

type ScrollToTargetProps = {
  targetId?: string
}

// Flag to track if we should prevent subsequent automatic scrolls
const SCROLL_PREVENTION_TIMEOUT = 500
let shouldPreventAutomaticScrolls = false

/**
 * Sets a flag to prevent subsequent automatic scrolls for a short period.
 * Useful when you want to perform an explicit scroll and prevent any automatic
 * scrolls that might be triggered by related actions (like filter updates).
 */
export const setScrollPrevention = () => {
  shouldPreventAutomaticScrolls = true
  // Clear the flag after a short delay to allow for subsequent filter updates
  setTimeout(() => {
    shouldPreventAutomaticScrolls = false
  }, SCROLL_PREVENTION_TIMEOUT)
}

export const scrollToTarget = (props?: ScrollToTargetProps) => {
  const { targetId } = props || {}

  // If this is an explicit scroll with targetId, just execute
  if (targetId) {
    scrollToId(targetId)
    return
  }

  // If this is an automatic scroll and we should prevent it, skip
  if (shouldPreventAutomaticScrolls) {
    return
  }

  if (typeof window === 'undefined') return

  const isMobile = window.innerWidth < 768

  if (isMobile) {
    scrollToId('representativeExampleHeading')
  } else {
    scrollToSortFilterOrFallback()
  }
}

const onScroll = (element: Element) => {
  // Mark scroll as programmatic before scrolling
  markScrollAsProgrammatic()

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
  })
}

// --- Internal helpers: scrollToId and scrollToSortFilterOrFallback --- //
const scrollToId = (id: string) => {
  const element = document.querySelector(`#${id}`)
  if (!element) return

  onScroll(element)
}

const scrollToSortFilterOrFallback = () => {
  const element = document.querySelector('#sort-filter') // Get sort filter element as reference to align the scroll position

  if (!element) {
    markScrollAsProgrammatic()
    window.scrollTo({ top: 0, behavior: 'smooth' }) // Fallback to fixed position if sort filter not found
    return
  }

  // Check if sort filter is already well-positioned
  const rect = element.getBoundingClientRect() // Get current position relative to viewport
  const targetViewportPosition = 50 // Target position to align the scroll position
  const acceptableRange = 30 // Allow 30px tolerance
  const isInAcceptablePosition =
    rect.top >= targetViewportPosition - acceptableRange &&
    rect.top <= targetViewportPosition + acceptableRange
  if (isInAcceptablePosition) return // Don't scroll if already well-positioned

  // Check if page is too short
  const documentHeight = document.documentElement.scrollHeight
  const viewportHeight = window.innerHeight

  if (documentHeight <= viewportHeight * 1.2) {
    markScrollAsProgrammatic()
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  onScroll(element) // Scroll to sort filter

  // Adjust scroll position to get the 50px offset we want, set a small delay to let scrollIntoView complete
  setTimeout(() => {
    const newRect = element.getBoundingClientRect()
    const adjustment = newRect.top - targetViewportPosition
    markScrollAsProgrammatic()
    window.scrollBy({ top: adjustment, behavior: 'smooth' })
  }, 100)
}
